import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Sparkles, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, statusLabel } from "@/components/StatusBadge";
import { RecordsTable } from "@/components/records/RecordsTable";

const KEYS = ["q", "record_type", "status", "tag", "date_from", "date_to", "mine", "office"];
const sel = "h-9 rounded-md border border-slate-300 bg-white text-sm px-2";

const AISearch = ({ onApply }) => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [s, setS] = useState(null);
  const go = async (e) => {
    e.preventDefault(); setBusy(true);
    try { setS((await api.post("/ai/search", { query: q })).data); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
      <form onSubmit={go} className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder='Ask: "approved payments to Acme last month"' className="bg-white" data-testid="ai-search-input" />
        <Button type="submit" variant="outline" disabled={busy || q.length < 3} data-testid="ai-search-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-amber-600" />}<span className="ml-1.5 hidden sm:inline">Interpret</span></Button>
      </form>
      {s && (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 text-sm fade-up" data-testid="ai-search-interpretation">
          <span className="text-slate-700">{s.explanation}</span>
          <div className="flex gap-2 sm:ml-auto">
            <Button size="sm" onClick={() => { onApply(s); setS(null); }} data-testid="ai-search-apply-button">Apply these filters</Button>
            <Button size="sm" variant="ghost" onClick={() => setS(null)}><X className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Records() {
  const { meta } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const f = Object.fromEntries(KEYS.map((k) => [k, params.get(k) || ""]));
  const page = Number(params.get("page") || 1);
  const key = params.toString();
  useEffect(() => {
    setQ(params.get("q") || "");
    api.get("/records", { params: { ...f, page } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const setF = (patch) => {
    const n = { ...f, ...patch };
    const p = {};
    KEYS.forEach((k) => n[k] && (p[k] = n[k]));
    setParams(p);
  };
  const types = Object.entries(meta.record_types).filter(([, t]) => t.can_view);
  const canCreate = types.some(([, t]) => t.can_create);
  const officeName = meta.offices.find((o) => o.id === f.office)?.name;
  const title = [officeName, f.record_type ? meta.record_types[f.record_type]?.label : !officeName && "All records"].filter(Boolean).join(" · ");
  return (
    <div>
      <PageHeader eyebrow="Registry" title={title} testid="records-title">
        {canCreate && <Button asChild data-testid="records-new-button"><Link to={`/records/new${f.record_type || f.office ? "?" : ""}${new URLSearchParams(Object.fromEntries([["type", f.record_type], ["office", f.office]].filter(([, v]) => v))).toString()}`}><Plus className="w-4 h-4 mr-1" />Register record</Link></Button>}
      </PageHeader>
      <div className="space-y-3 mb-4">
        <AISearch onApply={(s) => setF({ q: s.q, record_type: s.record_type, status: s.status, date_from: s.date_from, date_to: s.date_to })} />
        <div className="flex flex-wrap gap-2 items-center" data-testid="records-filters">
          <form onSubmit={(e) => { e.preventDefault(); setF({ q }); }} className="flex-1 min-w-[200px]">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keyword or reference (e.g. FIN/2026/)" className="bg-white" data-testid="records-search-input" />
          </form>
          {meta.offices.length > 1 && (
            <select className={sel} value={f.office} onChange={(e) => setF({ office: e.target.value })} data-testid="filter-office">
              <option value="">All offices</option>{meta.offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          <select className={sel} value={f.record_type} onChange={(e) => setF({ record_type: e.target.value })} data-testid="filter-type">
            <option value="">All types</option>{types.map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
          </select>
          <select className={sel} value={f.status} onChange={(e) => setF({ status: e.target.value })} data-testid="filter-status">
            <option value="">All statuses</option>{meta.statuses.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </select>
          <select className={sel} value={f.mine} onChange={(e) => setF({ mine: e.target.value })} data-testid="filter-mine">
            <option value="">Everyone</option><option value="created">Created by me</option><option value="assigned">Assigned to me</option>
          </select>
          <Input type="date" value={f.date_from} onChange={(e) => setF({ date_from: e.target.value })} className="w-auto bg-white" data-testid="filter-date-from" />
          <Input type="date" value={f.date_to} onChange={(e) => setF({ date_to: e.target.value })} className="w-auto bg-white" data-testid="filter-date-to" />
          {KEYS.some((k) => f[k]) && <Button variant="ghost" size="sm" onClick={() => setParams({})} data-testid="filters-clear">Clear</Button>}
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        {!data ? <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : <>
          <div className="px-4 py-2.5 text-xs text-slate-500 border-b border-slate-100" data-testid="records-count">{data.total} result{data.total !== 1 && "s"}{data.mode === "relevance" && " · sorted by relevance"}</div>
          <RecordsTable items={data.items} />
          {data.total > data.limit && (
            <div className="flex justify-end gap-2 p-3">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setParams({ ...Object.fromEntries(params), page: page - 1 })} data-testid="page-prev">Previous</Button>
              <Button size="sm" variant="outline" disabled={page * data.limit >= data.total} onClick={() => setParams({ ...Object.fromEntries(params), page: page + 1 })} data-testid="page-next">Next</Button>
            </div>
          )}
        </>}
      </div>
    </div>
  );
}
